import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Info as InfoIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { AuthService } from '../services/authService';
import { AppColors } from '../theme/colors';
import { useAuth, useCompany } from '../hooks/useAuth';
import { TermsNotice } from '../components/TermsNotice';

export const AdminLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { company } = useCompany(user?.uid || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // パスワードリセット用の状態
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [remember, setRemember] = useState(false);

  // 会社情報が取得できたら、会社IDを自動入力（退会済みの場合は除く）
  useEffect(() => {
    if (company?.corporateCode) {
      // 退会処理が完了している場合は自動入力しない（既に入力されている場合はクリア）
      const cancellationStatus = (company as any).cancellationStatus;
      if (cancellationStatus === 'completed') {
        // 退会済みの会社IDが入力されている場合はクリア
        if (companyId === company.corporateCode) {
          setCompanyId('');
        }
      } else {
        setCompanyId(company.corporateCode);
      }
    } else if (!company) {
      // 会社情報が取得できない場合（退会済みなど）は、既に入力されている会社IDをクリアしない
      // （ユーザーが手動で入力した可能性があるため）
    }
  }, [company, companyId]);

  // 初期表示時に管理者ログイン情報をCookieから取得して自動入力
  useEffect(() => {
    // 退会済みの会社の場合はCookieからも取得しない
    const cancellationStatus = company ? (company as any).cancellationStatus : null;
    if (cancellationStatus === 'completed') {
      return;
    }

    const endpoint = '/api/getAdminLoginRemember';
    fetch(endpoint, { method: 'GET', credentials: 'include' })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data && data.exists) {
          // 退会済みでない場合のみ自動入力
          const currentCancellationStatus = company ? (company as any).cancellationStatus : null;
          if (currentCancellationStatus !== 'completed') {
            setCompanyId(data.companyId || (company?.corporateCode && currentCancellationStatus !== 'completed' ? company.corporateCode : ''));
            setEmail(data.email || '');
            setPassword(data.password || '');
            setRemember(true);
          }
        }
      })
      .catch(() => {});
  }, [company]);

  const handleAdminLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    
    
    try {
      const result = await AuthService.signInWithEmailPassword(companyId.trim(), email, password);

      // 管理者ユーザーID を localStorage に保存（useAuth で復元用）
      // Firestore ドキュメント ID を優先し、なければ Firebase UID を使用
      if (result.user?.uid || result.user?.firebaseUid) {
        const docId = result.user?.uid;
        const fallbackUid = result.user?.firebaseUid;
        const storeId = docId || fallbackUid;
        if (storeId) {
          localStorage.setItem('adminUserId', storeId);
        }
      }

      // 記憶設定の反映（HttpOnly Cookie, 1ヶ月）
      try {
        if (remember) {
          await fetch('/api/setAdminLoginRemember', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ companyId: companyId.trim(), email, password }),
            credentials: 'include',
          });
        } else {
          await fetch('/api/clearAdminLoginRemember', { method: 'POST', credentials: 'include' });
        }
      } catch (_) {}

      // 管理者ログイン成功時に管理者画面に遷移
      // replace: trueで履歴を置き換えて戻れないようにする
      navigate('/admin', { replace: true });
    } catch (err: any) {
      console.error('管理者ログインエラー:', err);
      setError(err.message || '管理者ログインに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const [resetCompanyId, setResetCompanyId] = useState('');

  const handleForgotPassword = () => {
    setResetDialogOpen(true);
    setResetCompanyId('');
    setResetEmail('');
  };

  const handleResetDialogClose = () => {
    setResetDialogOpen(false);
    setResetCompanyId('');
    setResetEmail('');
  };

  const handleSendResetEmail = async () => {
    if (!resetCompanyId.trim()) {
      setSnackbarMessage('会社IDを入力してください');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!resetEmail.trim()) {
      setSnackbarMessage('メールアドレスを入力してください');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await AuthService.sendPasswordResetEmail(resetCompanyId.trim(), resetEmail.trim());
      setResetDialogOpen(false);
      setSnackbarMessage('パスワードリセットメールを送信しました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err: any) {
      setResetDialogOpen(false);
      setSnackbarMessage(err.message || 'パスワードリセットメールの送信に失敗しました。しばらく時間をおいてから再度お試しください');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleBack = () => {
    navigate('/home');
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'white', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <AppBar
        position="static"
        sx={{
          bgcolor: 'white',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {/*
          矢印の隣に今いる画面名（管理画面ログイン）を置くと、押した先が
          管理画面ログインであるように読めてしまう。行き先を文言にする。
          画面名は本文の「LiaVoice管理画面」で示している。

          未ログインのときは出さない。この画面が入口そのもので、戻り先がない。
        */}
        <Toolbar>
          {user && (
            <Box
              onClick={handleBack}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                color: 'black',
              }}
            >
              <IconButton edge="start" color="inherit" sx={{ color: 'black' }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                ホームへ
              </Typography>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* メインコンテンツ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: { xs: 'center', md: 'flex-start' },
          p: 2,
          pt: { xs: 2, md: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {/* ロゴとアプリ名 */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ height: { xs: 6, md: 0 } }} /> {/* スマホで少し余白、PCでいっぱい狭く */}
            <Box
              component="img"
              src="/icon.svg"
              alt="LiaVoice Icon"
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto',
                display: 'block',
              }}
            />
            <Box sx={{ height: 8 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                fontSize: '1.5rem', 
                fontWeight: 800, 
                color: AppColors.primary,
                letterSpacing: '1.0px',
                fontFamily: '"Nunito", "Noto Sans JP", sans-serif',
                ml: 0.5, // 少し右にずらす
              }}
            >
              LiaVoice管理画面
            </Typography>
            <Box sx={{ height: { xs: 4, md: 20 } }} /> {/* スマホで少し余白、PCでかなり広く */}
          </Box>

        <Box component="form" onSubmit={handleAdminLogin}>
          {/* 会社ID入力フィールド */}
          <TextField
            fullWidth
            label="会社ID"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            required
            disabled={loading || !!company?.corporateCode}
            helperText={company?.corporateCode ? '現在ログイン中の会社IDが自動入力されています' : ''}
            InputProps={{
              startAdornment: (
                <PersonIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 500,
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '1rem',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: 8,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                  borderWidth: 2,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="メールアドレス"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <PersonIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
            }}
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 500,
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '1rem',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: 8,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                  borderWidth: 2,
                },
              },
            }}
          />

          <TextField
            fullWidth
            label="パスワード"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={loading}
            InputProps={{
              startAdornment: (
                <LockIcon sx={{ color: AppColors.textSecondary, mr: 1 }} />
              ),
              endAdornment: (
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  disabled={loading}
                  sx={{ 
                    color: AppColors.textSecondary,
                    '&:hover': {
                      backgroundColor: AppColors.primaryLighter,
                    }
                  }}
                >
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiInputLabel-root': {
                fontSize: '0.875rem',
                fontWeight: 500,
              },
              '& .MuiOutlinedInput-input': {
                fontSize: '1rem',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: 8,
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: AppColors.primary,
                  borderWidth: 2,
                },
              },
            }}
          />

          <Box sx={{ textAlign: 'right', mb: 4 }}>
            <Button
              onClick={handleForgotPassword}
              sx={{
                color: AppColors.primary,
                textTransform: 'none',
                fontSize: 14,
                textDecoration: 'underline',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              パスワードを忘れた場合
            </Button>
          </Box>

          {/* 記憶するチェックボックス */}
          <Box sx={{ mb: 2 }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: loading ? 'not-allowed' : 'pointer' }}>
              <input
                type="checkbox"
                checked={remember}
                disabled={loading}
                onChange={(e) => setRemember(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              ログイン情報を記憶する
            </label>
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !companyId.trim() || !email.trim() || !password.trim()}
            sx={{
              height: 56,
              bgcolor: AppColors.primary,
              color: 'white',
              borderRadius: 3,
              fontSize: 16,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                bgcolor: AppColors.primaryDark,
              },
              '&:disabled': {
                bgcolor: AppColors.border,
                color: AppColors.textSecondary,
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : '管理者アカウントでログイン'}
          </Button>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>

        {/* 管理者通知 */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: `${AppColors.warning}20`,
            borderRadius: 3,
            border: `1px solid ${AppColors.warning}50`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <InfoIcon sx={{ color: AppColors.warning, fontSize: 20, mr: 1.5, mt: 0.5 }} />
            <Box>
              <Typography
                sx={{
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  lineHeight: 1.7,
                }}
              >
                管理者アカウントが必要です。
                <br />
                アカウントをお持ちでない場合は、システム管理者にお問い合わせください。
              </Typography>
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: 14,
                  color: AppColors.textSecondary,
                  lineHeight: 1.7,
                }}
              >
                投稿される方は、管理者からQRコード・専用URLを共有してもらってください。
              </Typography>
            </Box>
          </Box>
        </Box>

        <TermsNotice />
          </Box>
      </Box>

      {/* パスワードリセットダイアログ */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleResetDialogClose}
        maxWidth="xs"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle>パスワードリセット</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2, fontSize: 14, color: AppColors.textSecondary }}>
            パスワードリセット用のメールを送信します。会社IDとメールアドレスを入力してください。
          </Typography>
          <TextField
            fullWidth
            placeholder="会社ID"
            value={resetCompanyId}
            onChange={(e) => setResetCompanyId(e.target.value)}
            autoFocus
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: AppColors.border,
                },
                '&:hover fieldset': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: AppColors.primary,
                },
              },
            }}
          />
          <TextField
            fullWidth
            placeholder="メールアドレス"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            type="email"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '& fieldset': {
                  borderColor: AppColors.border,
                },
                '&:hover fieldset': {
                  borderColor: AppColors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: AppColors.primary,
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleResetDialogClose}
            sx={{
              color: AppColors.textSecondary,
              textTransform: 'none',
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleSendResetEmail}
            sx={{
              color: AppColors.primary,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            送信
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};